import { TimezonePicker } from '@tigergraph/app-ui-lib/timezonepicker';
import { WorkspaceT, WorkSpaceType } from '@/pages/workgroup/type';
import { Button } from '@tigergraph/app-ui-lib/button';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Controller, FieldErrors, useForm, UseFormReturn } from 'react-hook-form';
import { FormControlOverrides, FormControlProps } from 'baseui/form-control';
import { FormControl as BaseFormControl } from '@tigergraph/app-ui-lib/form-control';
import { mergeOverrides } from 'baseui';
import { Overrides } from 'baseui/overrides';
import { CustomTheme } from '@tigergraph/app-ui-lib/Theme';
import { Schedule, ScheduleData, scheduleDescription, ScheduleWeekly, WeekDays } from './type';
import { roundTo30Minutes } from './hook';
import { expand } from 'inline-style-expand-shorthand';

import { Select } from '@tigergraph/app-ui-lib/select';
import ParagraphLabel from '@/components/PagagraphLabel';
import { formatPricePerHour } from '@/utils/format';
import currency from 'currency.js';
import { DatePicker } from '@tigergraph/app-ui-lib/datepicker';
import { TimePicker } from '@tigergraph/app-ui-lib/timepicker';
import { useWorkspaceContext } from '@/contexts/workspaceContext';
import { StatefulTipsPopover } from '@/components/tipsPopover';
import { TRIGGER_TYPE } from 'baseui/popover';
import mediaQuery from '@/lib/breakpoints';

export default function ScheduleForm({
  onClose,
  workspace,
  editSchedule,
  workspaceTypes,
  onSubmit,
}: {
  onClose: () => void;
  workspace: WorkspaceT;
  editSchedule?: Schedule;
  workspaceTypes: WorkSpaceType[];
  onSubmit: (data: ScheduleData) => void;
}) {
  const form = useForm<ScheduleData>({
    defaultValues: editSchedule
      ? editSchedule
      : {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          workspace_type_name: workspaceTypes[0].typeName,
          repeat: 'NOREPEAT',
          date: new Date(),
          time: roundTo30Minutes(new Date()),
          // @ts-ignore
          repeat_on: [],
        },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    getValues,
  } = form;

  const repeat = watch('repeat');

  const [css, theme] = useStyletron();

  const { orgQuota } = useWorkspaceContext();
  const quotaFilterIndex = orgQuota?.max_workspace_memory
    ? workspaceTypes.findIndex((item) => parseInt(item.memory) > parseInt(orgQuota.max_workspace_memory))
    : -1;

  if (quotaFilterIndex > -1) {
    workspaceTypes = workspaceTypes.map((w, index) => ({
      ...w,
      disabled: index >= quotaFilterIndex,
    }));
  }

  // for RO, do not allow resize
  if (!workspace.is_rw) {
    workspaceTypes = workspaceTypes.filter((w) => w.typeName === workspace.workspace_type.typeName);
  }

  const options = [
    {
      typeName: 'Pause',
    },
  ].concat(workspaceTypes);

  return (
    <div
      className={css({
        padding: '24px 16px 16px',
        backgroundColor: theme.colors['background.secondary'],
        borderRadius: '4px',
        border: `1px solid ${theme.colors['border.tertiary']}`,
      })}
    >
      <FormControl label="Timezone">
        <Controller
          control={control}
          name="timezone"
          render={({ field: { onChange, value, ...props } }) => (
            <TimezonePicker value={value} onChange={({ id }) => onChange(id)} {...props} size="compact" />
          )}
        />
      </FormControl>
      <div
        className={css({
          display: 'flex',
          gap: '8px',
          [mediaQuery.xsmall]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 0,
          },
        })}
      >
        <div
          className={css({
            width: '108px',
            [mediaQuery.xsmall]: {
              width: '100%',
            },
          })}
        >
          <FormControl label="Repeat">
            <Controller
              control={control}
              name="repeat"
              render={({ field: { value, onChange, ref, ...field } }) => {
                return (
                  <Select
                    size="compact"
                    options={[
                      {
                        id: 'NOREPEAT',
                        label: 'No Repeat',
                      },
                      {
                        id: 'DAILY',
                        label: 'Daily',
                      },
                      {
                        id: 'WEEKDAY',
                        label: 'Weekday',
                      },
                      {
                        id: 'WEEKLY',
                        label: 'Weekly',
                      },
                    ]}
                    value={[{ id: value }]}
                    labelKey="label"
                    onChange={(params) => {
                      onChange(params.value[0].id);
                    }}
                    clearable={false}
                    inputRef={ref}
                    {...field}
                    searchable={false}
                  />
                );
              }}
            />
          </FormControl>
        </div>
        {repeat === 'NOREPEAT' && <NoRepeat form={form} />}
        {(repeat === 'DAILY' || repeat === 'WEEKDAY') && <DailyOrWeekday form={form} />}
        {repeat === 'WEEKLY' && <Weekly form={form} />}
      </div>
      <FormControl label="Workspace Size">
        <Controller
          control={control}
          name="workspace_type_name"
          render={({ field: { value, onChange, ref, ...field } }) => {
            return (
              <Select
                size="compact"
                options={options}
                value={[{ id: value }]}
                onChange={(params) => {
                  onChange(params.value[0].typeName);
                }}
                clearable={false}
                inputRef={ref}
                {...field}
                searchable={false}
                getValueLabel={({ option }) => {
                  const workspaceType = options.find((item) => item.typeName === option.id);
                  // @ts-expect-error
                  return <WorkspaceLabel workspaceType={workspaceType} />;
                }}
                getOptionLabel={(option) => {
                  return (
                    <WorkspaceLabel
                      workspaceType={option.option as WorkSpaceType}
                      disabled={option.optionState.$disabled}
                    />
                  );
                }}
              />
            );
          }}
        />
      </FormControl>
      <ParagraphLabel
        className={css({
          color: theme.colors['text.secondary'],
        })}
      >
        {scheduleDescription(getValues())}
      </ParagraphLabel>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        })}
      >
        <Button kind="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="secondary"
          type="button"
          onClick={handleSubmit((data) => {
            onSubmit(data);
          })}
          overrides={{
            BaseButton: {
              style: {
                ...expand({
                  borderColor: theme.colors['background.brand.bold'],
                }),
              },
            },
          }}
        >
          OK
        </Button>
      </div>
    </div>
  );
}

function NoRepeat({ form }: { form: UseFormReturn<ScheduleData, any, undefined> }) {
  const [css] = useStyletron();
  const { control, watch } = form;

  const repeat = watch('repeat');
  if (repeat !== 'NOREPEAT') {
    return null;
  }

  return (
    <>
      <div
        className={css({
          flex: 1,
          [mediaQuery.xsmall]: {
            width: '100%',
          },
        })}
      >
        <FormControl label="Date">
          <Controller
            control={control}
            name="date"
            render={({ field: { onChange, ref, ...field } }) => {
              return (
                <DatePicker
                  formatString={'yyyy/MM/dd'}
                  placeholder="YYYY/MM/DD"
                  onChange={({ date }) => {
                    onChange(date);
                  }}
                  {...field}
                  highlightedDate={new Date()}
                  size="compact"
                  overrides={{
                    InputWrapper: {
                      style: {
                        [mediaQuery.xsmall]: {
                          maxWidth: '100%',
                        },
                      },
                    },
                    Input: {
                      props: {
                        overrides: {
                          Root: {
                            style: () => {
                              return {
                                [mediaQuery.small]: {
                                  maxWidth: '100%',
                                },
                              };
                            },
                          },
                        },
                      },
                    },
                  }}
                />
              );
            }}
          />
        </FormControl>
      </div>
      <div
        className={css({
          flex: 1,
          [mediaQuery.xsmall]: {
            width: '100%',
          },
        })}
      >
        <FormControl label="Time">
          <Controller
            control={control}
            name="time"
            render={({ field: { onChange, ref, ...field } }) => {
              return (
                <TimePicker
                  onChange={(date) => {
                    onChange(date);
                  }}
                  {...field}
                  size="compact"
                  format="24"
                  step={30 * 60}
                />
              );
            }}
          />
        </FormControl>
      </div>
    </>
  );
}

function DailyOrWeekday({ form }: { form: UseFormReturn<ScheduleData, any, undefined> }) {
  const [css] = useStyletron();
  const { control, watch } = form;

  const repeat = watch('repeat');
  if (repeat !== 'DAILY' && repeat !== 'WEEKDAY') {
    return null;
  }

  return (
    <>
      <div
        className={css({
          flex: 1,
          [mediaQuery.xsmall]: {
            width: '100%',
          },
        })}
      >
        <FormControl label="Time">
          <Controller
            control={control}
            name="time"
            render={({ field: { onChange, ref, ...field } }) => {
              return (
                <TimePicker
                  onChange={(date) => {
                    onChange(date);
                  }}
                  {...field}
                  size="compact"
                  format="24"
                  step={30 * 60}
                />
              );
            }}
          />
        </FormControl>
      </div>
    </>
  );
}

function Weekly({ form }: { form: UseFormReturn<ScheduleData, any, undefined> }) {
  const [css] = useStyletron();
  const {
    control,
    watch,
    formState: { errors },
  } = form;

  const repeat = watch('repeat');
  if (repeat !== 'WEEKLY') {
    return null;
  }

  return (
    <>
      <div
        className={css({
          flex: 2,
          [mediaQuery.xsmall]: {
            width: '100%',
          },
        })}
      >
        <FormControl label="Repeat on" error={(errors as FieldErrors<ScheduleWeekly>).repeat_on?.message}>
          <Controller
            control={control}
            name="repeat_on"
            render={({ field: { value, onChange, ref, ...field } }) => {
              return (
                <Select
                  error={!!(errors as FieldErrors<ScheduleWeekly>).repeat_on}
                  size="compact"
                  overrides={{
                    // tweak tag style so it will not change height when add tag to select input.
                    Tag: {
                      props: {
                        overrides: {
                          Text: {
                            style: {
                              lineHeight: '12px',
                            },
                          },
                          Root: {
                            style: {
                              paddingTop: 0,
                              paddingBottom: 0,
                              height: '16px',
                              marginTop: '2px',
                              marginBottom: '2px',
                            },
                          },
                        },
                      },
                    },
                  }}
                  options={WeekDays}
                  value={value.map((v) => ({ id: v }))}
                  labelKey="label"
                  onChange={(params) => {
                    onChange(params.value.map((v) => v.id));
                  }}
                  clearable={true}
                  inputRef={ref}
                  {...field}
                  searchable={false}
                  multi={true}
                  closeOnSelect={false}
                />
              );
            }}
          />
        </FormControl>
      </div>
      <div
        className={css({
          flex: 1,
          [mediaQuery.xsmall]: {
            width: '100%',
          },
        })}
      >
        <FormControl label="Time">
          <Controller
            control={control}
            name="time"
            render={({ field: { onChange, ref, ...field } }) => {
              return (
                <TimePicker
                  onChange={(date) => {
                    onChange(date);
                  }}
                  {...field}
                  size="compact"
                  format="24"
                  step={30 * 60}
                />
              );
            }}
          />
        </FormControl>
      </div>
    </>
  );
}

const WorkspaceLabel = ({ workspaceType, disabled }: { workspaceType?: WorkSpaceType; disabled?: boolean }) => {
  const [css, theme] = useStyletron();
  if (!workspaceType) {
    return null;
  }
  const content = (
    <ParagraphLabel
      className={css({
        opacity: disabled ? 0.5 : 1,
      })}
    >
      <span
        className={css({
          color: theme.colors['dropdown.text'],
          fontWeight: 500,
        })}
      >
        {workspaceType.typeName}
      </span>
      {workspaceType.typeName === 'Pause' ? null : (
        <span
          className={css({
            color: theme.colors['text.secondary'],
          })}
        >{` (vCPUs: ${workspaceType.cpu}, RAM: ${workspaceType.memory}, Cost: ${formatPricePerHour(
          currency(workspaceType.cost_per_hour)
        )})`}</span>
      )}
    </ParagraphLabel>
  );

  if (!disabled) {
    return content;
  }

  return (
    <StatefulTipsPopover
      content="The size is not available within your organization's current quota. Please contact sales to request an upgrade."
      triggerType={TRIGGER_TYPE.hover}
      focusLock={true}
      overrides={{
        Body: {
          style: {
            maxWidth: '450px',
          },
        },
      }}
    >
      {content}
    </StatefulTipsPopover>
  );
};

const formControlOverrides: FormControlOverrides = {
  Label: {
    style: ({ $theme }) => {
      const theme = $theme as CustomTheme;
      return {
        fontSize: '12px',
        lineHeight: '16px',
        color: theme.colors['dropdown.text'],
        marginTop: '-8px',
        marginBottom: '-4px',
      };
    },
  },
  ControlContainer: {
    style: {
      marginBottom: '8px',
    },
  },
};

export function FormControl({ overrides, error, ...props }: FormControlProps) {
  return (
    <BaseFormControl
      {...props}
      error={error || undefined}
      overrides={mergeOverrides(formControlOverrides as Overrides<unknown>, overrides as Overrides<unknown>)}
    />
  );
}
